import type { Interview, InviteContext } from "./api";
import { BUSINESS_TIMEZONE } from "./dates";

/**
 * Calendar invites for interviews, built in the browser. The recruiter attaches the file to
 * their own email: the candidate's and the client's are separate files so neither sees the other's
 * address. Each audience's event keeps one UID, and the interview's sequence goes up on every
 * change, so a newer file updates (or cancels) the event already in someone's calendar.
 */
export type Audience = "candidate" | "client";

const FORMAT: Record<Interview["format"], string> = { video: "Video call", phone: "Phone call", in_person: "In person" };

/** "2026-10-15" + "14:00" in UK time → the moment, whatever the browser's own timezone. */
export function londonToUtc(date: string, time: string): Date {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const [hh, mm] = time.split(":").map(Number) as [number, number];
  const wall = Date.UTC(y, m - 1, d, hh, mm);
  const offset = (t: number) => {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: BUSINESS_TIMEZONE,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).formatToParts(new Date(t));
    const get = (k: string) => Number(parts.find((p) => p.type === k)!.value);
    return Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute")) - t;
  };
  let t = wall - offset(wall);
  t = wall - offset(t); // twice, for the hour either side of a clock change
  return new Date(t);
}

/** The UK date and time of a moment, for filling the form back in: ["2026-10-15", "14:00"]. */
export function utcToLondon(at: string | Date): [string, string] {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: BUSINESS_TIMEZONE, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).formatToParts(new Date(at));
  const get = (k: string) => parts.find((p) => p.type === k)!.value;
  return [`${get("year")}-${get("month")}-${get("day")}`, `${get("hour")}:${get("minute")}`];
}

const slotFmt = new Intl.DateTimeFormat("en-GB", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit", timeZone: BUSINESS_TIMEZONE });
/** "Thursday 15 October at 14:00 (UK time)". */
export const slotText = (at: string | Date) => `${slotFmt.format(new Date(at))} (UK time)`;

const stamp = (d: Date) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
const endOf = (i: Pick<Interview, "startsAt" | "durationMinutes">) => new Date(new Date(i.startsAt).getTime() + i.durationMinutes * 60_000);

/** Text value escaping (RFC 5545 3.3.11). */
export const escapeText = (s: string) => s.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");
/** CN parameter: quoted, without characters a quoted parameter can't hold. */
const cn = (s: string) => `"${s.replace(/["\r\n]/g, "")}"`;

/** Lines longer than 75 octets continue on the next line after a space (RFC 5545 3.1), never splitting a character. */
export function fold(line: string): string {
  const enc = new TextEncoder();
  const out: string[] = [];
  let current = "";
  let bytes = 0;
  for (const ch of line) {
    const n = enc.encode(ch).length;
    if (bytes + n > (out.length ? 74 : 75)) {
      out.push(current);
      current = "";
      bytes = 0;
    }
    current += ch;
    bytes += n;
  }
  out.push(current);
  return out.join("\r\n ");
}

/** The one person an audience's invite goes to (the candidate, or the job's hiring contact). */
function person(ctx: InviteContext, audience: Audience): { name: string; email: string | null } | null {
  if (audience === "candidate") return { name: ctx.candidate.fullName, email: ctx.candidate.email };
  return ctx.contact ? { name: ctx.contact.fullName, email: ctx.contact.email } : null;
}

export function inviteText(i: Interview, ctx: InviteContext, audience: Audience | "me") {
  const company = ctx.company.name;
  const summary =
    audience === "candidate"
      ? `Interview: ${ctx.job.title} at ${company}`
      : audience === "client"
        ? `Interview: ${ctx.candidate.fullName} for ${ctx.job.title}`
        : `Interview: ${ctx.candidate.fullName} with ${company} (${ctx.job.title})`;
  const lines = [
    audience === "candidate" ? `${i.stageName} with ${company} for the ${ctx.job.title} role.` : `${i.stageName} with ${ctx.candidate.fullName} for ${ctx.job.title}.`,
    `${FORMAT[i.format]}${i.location ? `: ${i.location}` : ""}`,
    i.interviewers ? `With: ${i.interviewers}` : "",
    i.notes,
    "",
    `Arranged by ${ctx.recruiter.name}, ${ctx.agency} (${ctx.recruiter.email}).`,
  ];
  return { summary, description: lines.filter((l, n) => l !== "" || n === 4).join("\n").trim() };
}

/** The .ics file for one audience. Cancelled interviews produce a cancellation. */
export function buildIcs(i: Interview, ctx: InviteContext, audience: Audience, now = new Date()): string {
  const who = person(ctx, audience);
  const cancelled = i.status === "cancelled";
  const withAttendee = !!who?.email;
  const { summary, description } = inviteText(i, ctx, audience);
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//KuduRMS//Interviews//EN",
    "CALSCALE:GREGORIAN",
    // With someone to invite it's a meeting request (they can accept); without, a plain event.
    `METHOD:${cancelled ? "CANCEL" : withAttendee ? "REQUEST" : "PUBLISH"}`,
    "BEGIN:VEVENT",
    `UID:interview-${i.id}-${audience}@kudurms`,
    `SEQUENCE:${i.sequence}`,
    `DTSTAMP:${stamp(now)}`,
    `DTSTART:${stamp(new Date(i.startsAt))}`,
    `DTEND:${stamp(endOf(i))}`,
    `SUMMARY:${escapeText(cancelled ? `Cancelled: ${summary}` : summary)}`,
    `DESCRIPTION:${escapeText(description)}`,
    ...(i.location ? [`LOCATION:${escapeText(i.location)}`] : []),
    `ORGANIZER;CN=${cn(ctx.recruiter.name)}:mailto:${ctx.recruiter.email}`,
    ...(withAttendee ? [`ATTENDEE;CN=${cn(who!.name)};ROLE=REQ-PARTICIPANT;PARTSTAT=NEEDS-ACTION;RSVP=TRUE:mailto:${who!.email}`] : []),
    `STATUS:${cancelled ? "CANCELLED" : "CONFIRMED"}`,
    "TRANSP:OPAQUE",
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  return lines.map(fold).join("\r\n") + "\r\n";
}

const linkStamp = (d: Date) => stamp(d);

/** Adds the interview to the recruiter's own Google Calendar. */
export function googleCalendarUrl(i: Interview, ctx: InviteContext): string {
  const { summary, description } = inviteText(i, ctx, "me");
  const q = new URLSearchParams({ action: "TEMPLATE", text: summary, dates: `${linkStamp(new Date(i.startsAt))}/${linkStamp(endOf(i))}`, details: description, location: i.location });
  return `https://calendar.google.com/calendar/render?${q}`;
}

/** Adds it to the recruiter's own Outlook (Microsoft 365) calendar. */
export function outlookCalendarUrl(i: Interview, ctx: InviteContext): string {
  const { summary, description } = inviteText(i, ctx, "me");
  const q = new URLSearchParams({ path: "/calendar/action/compose", rru: "addevent", subject: summary, startdt: new Date(i.startsAt).toISOString(), enddt: endOf(i).toISOString(), body: description, location: i.location });
  return `https://outlook.office.com/calendar/0/deeplink/compose?${q}`;
}

/** Saves an .ics file. */
export function downloadIcs(filename: string, ics: string) {
  const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** A draft email in the recruiter's own mail app, to attach the downloaded invite to. */
export function inviteEmail(i: Interview, ctx: InviteContext, audience: Audience): string {
  const who = person(ctx, audience);
  const first = who?.name.replace(/^(dr|mr|mrs|ms|miss|prof)\.?\s+/i, "").split(" ")[0] ?? "";
  const cancelled = i.status === "cancelled";
  const { summary, description } = inviteText(i, ctx, audience);
  const subject = cancelled ? `Cancelled: ${summary}` : `${summary}, ${slotText(i.startsAt)}`;
  const body = cancelled
    ? `Hi ${first},\n\nI'm sorry, the interview on ${slotText(i.startsAt)} is cancelled. I've attached a cancellation for your calendar and will be in touch about next steps.\n\n${ctx.recruiter.name}`
    : `Hi ${first},\n\nConfirming the interview on ${slotText(i.startsAt)}.\n\n${description.split("\n\nArranged by")[0]}\n\nI've attached a calendar invite.\n\n${ctx.recruiter.name}`;
  return `mailto:${who?.email ?? ""}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
